import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMonthlyReport, getYearlySummary, compareEntries, compareRanges } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';

const monthNames = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];

export default function Reports() {
  const { selectedFactory } = useAuth();
  const [tab, setTab] = useState('monthly');

  // Monthly
  const [mYear, setMYear] = useState(new Date().getFullYear());
  const [mMonth, setMMonth] = useState(new Date().getMonth() + 1);
  const [monthData, setMonthData] = useState(null);
  const [mLoading, setMLoading] = useState(false);

  // Yearly
  const [yYear, setYYear] = useState(new Date().getFullYear());
  const [yearData, setYearData] = useState(null);
  const [yLoading, setYLoading] = useState(false);

  // Compare
  const [compareMode, setCompareMode] = useState('days'); // 'days' or 'ranges'
  const [compareDates, setCompareDates] = useState(['', '']);
  const [compareData, setCompareData] = useState(null);
  const [cLoading, setCLoading] = useState(false);

  // Range compare
  const [compareRangesInput, setCompareRangesInput] = useState([{ from: '', to: '' }, { from: '', to: '' }]);
  const [rangeCompareData, setRangeCompareData] = useState(null);
  const [rLoading, setRLoading] = useState(false);

  const loadMonthly = async () => {
    if (!selectedFactory) return;
    setMLoading(true);
    try {
      const res = await getMonthlyReport(selectedFactory.id, mYear, mMonth);
      setMonthData(res.data);
    } catch (err) { console.error(err); }
    finally { setMLoading(false); }
  };

  const loadYearly = async () => {
    if (!selectedFactory) return;
    setYLoading(true);
    try {
      const res = await getYearlySummary(selectedFactory.id, yYear);
      setYearData(res.data);
    } catch (err) { console.error(err); }
    finally { setYLoading(false); }
  };

  const loadCompare = async () => {
    if (!selectedFactory) return;
    const valid = compareDates.filter(Boolean);
    if (valid.length < 2) return;
    setCLoading(true);
    try {
      const res = await compareEntries(selectedFactory.id, valid);
      setCompareData(res.data);
    } catch (err) { console.error(err); }
    finally { setCLoading(false); }
  };

  const loadRangeCompare = async () => {
    if (!selectedFactory) return;
    const valid = compareRangesInput.filter(r => r.from && r.to);
    if (valid.length < 2) return;
    setRLoading(true);
    try {
      const res = await compareRanges(selectedFactory.id, valid);
      setRangeCompareData(res.data);
    } catch (err) { console.error(err); }
    finally { setRLoading(false); }
  };

  const yearChartData = yearData?.monthly_breakdown?.map(r => ({
    month: monthNames[r.month - 1],
    total: parseFloat(r.monthly_total),
    days: parseInt(r.working_days)
  })) || [];

  const dailyChartData = monthData?.daily_breakdown?.map(d => ({
    day: parseInt(d.entry_date?.split('-')[2]),
    total: parseFloat(d.daily_total)
  })) || [];

  const getPrintSubtitle = () => {
    if (tab === 'monthly' && monthData) return `Μηνιαία Αναφορά — ${monthNames[monthData.month - 1]} ${monthData.year}`;
    if (tab === 'yearly' && yearData) return `Ετήσια Αναφορά — ${yearData.year}`;
    if (tab === 'compare' && (compareData || rangeCompareData)) return compareMode === 'ranges' ? 'Σύγκριση Περιόδων' : 'Σύγκριση Ημερομηνιών';
    return 'Αναφορές';
  };

  const canPrint = (tab === 'monthly' && monthData) || (tab === 'yearly' && yearData) || (tab === 'compare' && (compareData || rangeCompareData));

  return (
    <>
      <div className="print-header" style={{ display: 'none' }}>
        <h1>🏭 Factory Tracker — {selectedFactory?.name}</h1>
        <p>{getPrintSubtitle()}</p>
      </div>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>Αναφορές</h2>
            <p>Αναλυτικά στοιχεία παραγωγής και εσόδων</p>
          </div>
          {canPrint && (
            <button className="btn btn-secondary no-print" onClick={() => window.print()}>🖨 Εκτύπωση / PDF</button>
          )}
        </div>
      </div>
      <div className="page-body">
        <div className="tabs">
          {[['monthly', '📅 Μηνιαία'], ['yearly', '📆 Ετήσια'], ['compare', '⚖️ Σύγκριση']].map(([key, label]) => (
            <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {/* MONTHLY */}
        {tab === 'monthly' && (
          <div>
            <div className="card no-print" style={{ marginBottom: 20 }}>
              <div className="card-body" style={{ padding: '14px 20px' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Έτος</label>
                    <input type="number" className="form-input" value={mYear} onChange={e => setMYear(parseInt(e.target.value))} style={{ width: 100 }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Μήνας</label>
                    <select className="form-select" value={mMonth} onChange={e => setMMonth(parseInt(e.target.value))} style={{ width: 130 }}>
                      {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={loadMonthly} disabled={mLoading}>
                    {mLoading ? <span className="loading-spinner" /> : 'Εμφάνιση'}
                  </button>
                </div>
              </div>
            </div>

            {monthData && (
              <>
                <div className="stats-grid" style={{ marginBottom: 20 }}>
                  <div className="stat-card">
                    <div className="stat-card-label">Σύνολο Μήνα</div>
                    <div className="stat-card-value blue">€{parseFloat(monthData.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</div>
                    <div className="stat-card-sub">{monthNames[monthData.month - 1]} {monthData.year}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Εργάσιμες Μέρες</div>
                    <div className="stat-card-value">{monthData.daily_breakdown?.length || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Μ.Ο. Ημέρας</div>
                    <div className="stat-card-value">
                      {monthData.daily_breakdown?.length
                        ? `€${(parseFloat(monthData.grand_total) / monthData.daily_breakdown.length).toFixed(0)}`
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="grid-2">
                  <div className="card">
                    <div className="card-header"><span className="card-title">Ημερήσια Έσοδα</span></div>
                    <div className="card-body" style={{ padding: '16px 8px' }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dailyChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={v => `€${v}`} width={55} />
                          <Tooltip formatter={v => [`€${v.toFixed(2)}`, 'Σύνολο']} labelFormatter={l => `Ημέρα ${l}`} />
                          <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header"><span className="card-title">Ανά Προϊόν</span></div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Προϊόν</th><th>Τεμ.</th><th>Σύνολο</th></tr></thead>
                        <tbody>
                          {monthData.product_breakdown?.map((p, i) => (
                            <tr key={i}>
                              <td><strong>{p.product_name}</strong></td>
                              <td>{parseFloat(p.total_quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}</td>
                              <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(p.total_revenue).toFixed(2)}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* YEARLY */}
        {tab === 'yearly' && (
          <div>
            <div className="card no-print" style={{ marginBottom: 20 }}>
              <div className="card-body" style={{ padding: '14px 20px' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Έτος</label>
                    <input type="number" className="form-input" value={yYear} onChange={e => setYYear(parseInt(e.target.value))} style={{ width: 100 }} />
                  </div>
                  <button className="btn btn-primary" onClick={loadYearly} disabled={yLoading}>
                    {yLoading ? <span className="loading-spinner" /> : 'Εμφάνιση'}
                  </button>
                </div>
              </div>
            </div>

            {yearData && (
              <>
                <div className="stats-grid" style={{ marginBottom: 20 }}>
                  <div className="stat-card">
                    <div className="stat-card-label">Σύνολο Έτους</div>
                    <div className="stat-card-value blue">€{parseFloat(yearData.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</div>
                    <div className="stat-card-sub">{yearData.year}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Ενεργοί Μήνες</div>
                    <div className="stat-card-value">{yearData.monthly_breakdown?.length || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Καλύτερος Μήνας</div>
                    <div className="stat-card-value" style={{ fontSize: 18 }}>
                      {yearData.monthly_breakdown?.length
                        ? monthNames[yearData.monthly_breakdown.reduce((a, b) => parseFloat(a.monthly_total) > parseFloat(b.monthly_total) ? a : b).month - 1]
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header"><span className="card-title">Μηνιαία Έσοδα {yearData.year}</span></div>
                  <div className="card-body" style={{ padding: '16px 8px' }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={yearChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94A3B8' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={v => `€${v}`} width={60} />
                        <Tooltip formatter={v => [`€${v.toFixed(2)}`, 'Σύνολο']} />
                        <Bar dataKey="total" fill="#2563EB" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="card" style={{ marginTop: 20 }}>
                  <div className="card-header"><span className="card-title">Μηνιαία Ανάλυση</span></div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Μήνας</th><th>Εργάσιμες</th><th>Σύνολο</th><th>Μ.Ο. Ημέρας</th></tr></thead>
                      <tbody>
                        {yearData.monthly_breakdown?.map(r => (
                          <tr key={r.month}>
                            <td><strong>{monthNames[r.month - 1]} {yearData.year}</strong></td>
                            <td>{r.working_days} μέρες</td>
                            <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(r.monthly_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</strong></td>
                            <td style={{ color: 'var(--gray-500)' }}>€{(parseFloat(r.monthly_total) / r.working_days).toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* COMPARE */}
        {tab === 'compare' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }} className="no-print">
              <button
                className={`btn ${compareMode === 'days' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => { setCompareMode('days'); setCompareData(null); setRangeCompareData(null); }}
              >📅 Σύγκριση Ημερών</button>
              <button
                className={`btn ${compareMode === 'ranges' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => { setCompareMode('ranges'); setCompareData(null); setRangeCompareData(null); }}
              >📆 Σύγκριση Περιόδων</button>
            </div>

            {/* DAY COMPARISON */}
            {compareMode === 'days' && (
              <>
                <div className="card no-print" style={{ marginBottom: 20 }}>
                  <div className="card-header"><span className="card-title">Επιλογή Ημερομηνιών για Σύγκριση</span></div>
                  <div className="card-body">
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      {compareDates.map((d, i) => (
                        <div key={i} className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Ημερομηνία {i + 1}</label>
                          <input type="date" className="form-input" value={d} onChange={e => {
                            const nd = [...compareDates]; nd[i] = e.target.value; setCompareDates(nd);
                          }} />
                        </div>
                      ))}
                      {compareDates.length < 5 && (
                        <button className="btn btn-secondary" onClick={() => setCompareDates([...compareDates, ''])}>+ Ημερομηνία</button>
                      )}
                      <button className="btn btn-primary" onClick={loadCompare} disabled={cLoading}>
                        {cLoading ? <span className="loading-spinner" /> : 'Σύγκριση'}
                      </button>
                    </div>
                  </div>
                </div>

                {compareData && (
                  <div className="grid-2">
                    {compareData.map((entry, i) => (
                      <div key={i} className="card">
                        <div className="card-header">
                          <span className="card-title">{format(new Date(entry.date), 'd MMMM yyyy', { locale: el })}</span>
                          <span className="badge badge-blue">€{parseFloat(entry.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="table-wrap">
                          {entry.items.length === 0 ? (
                            <div className="empty-state" style={{ padding: 30 }}><p>Δεν υπάρχουν δεδομένα</p></div>
                          ) : (
                            <table>
                              <thead><tr><th>Προϊόν</th><th>Τεμ.</th><th>Σύνολο</th></tr></thead>
                              <tbody>
                                {entry.items.map((item, j) => (
                                  <tr key={j}>
                                    <td>{item.product_name}</td>
                                    <td>{parseFloat(item.quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}</td>
                                    <td>€{parseFloat(item.total_price).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* RANGE COMPARISON */}
            {compareMode === 'ranges' && (
              <>
                <div className="card no-print" style={{ marginBottom: 20 }}>
                  <div className="card-header"><span className="card-title">Επιλογή Περιόδων για Σύγκριση</span></div>
                  <div className="card-body">
                    {compareRangesInput.map((r, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: i < compareRangesInput.length - 1 ? 12 : 0, paddingBottom: i < compareRangesInput.length - 1 ? 12 : 0, borderBottom: i < compareRangesInput.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-500)', minWidth: 70 }}>Περίοδος {i + 1}</div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Από</label>
                          <input type="date" className="form-input" value={r.from} onChange={e => {
                            const nr = [...compareRangesInput]; nr[i] = { ...nr[i], from: e.target.value }; setCompareRangesInput(nr);
                          }} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Έως</label>
                          <input type="date" className="form-input" value={r.to} onChange={e => {
                            const nr = [...compareRangesInput]; nr[i] = { ...nr[i], to: e.target.value }; setCompareRangesInput(nr);
                          }} />
                        </div>
                        {compareRangesInput.length > 2 && (
                          <button className="btn btn-danger btn-sm" onClick={() => {
                            setCompareRangesInput(compareRangesInput.filter((_, j) => j !== i));
                          }}>✕</button>
                        )}
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                      {compareRangesInput.length < 5 && (
                        <button className="btn btn-secondary" onClick={() => setCompareRangesInput([...compareRangesInput, { from: '', to: '' }])}>+ Περίοδος</button>
                      )}
                      <button className="btn btn-primary" onClick={loadRangeCompare} disabled={rLoading}>
                        {rLoading ? <span className="loading-spinner" /> : 'Σύγκριση'}
                      </button>
                    </div>
                  </div>
                </div>

                {rangeCompareData && (
                  <div className="grid-2">
                    {rangeCompareData.map((range, i) => (
                      <div key={i} className="card">
                        <div className="card-header">
                          <span className="card-title">
                            {format(new Date(range.from), 'd MMM yyyy', { locale: el })} — {format(new Date(range.to), 'd MMM yyyy', { locale: el })}
                          </span>
                          <span className="badge badge-blue">€{parseFloat(range.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-100)' }}>
                          {range.working_days} εργάσιμες μέρες • Μ.Ο. ημέρας: €{range.working_days > 0 ? (parseFloat(range.grand_total) / range.working_days).toFixed(2) : '0.00'}
                        </div>
                        <div className="table-wrap">
                          {range.items.length === 0 ? (
                            <div className="empty-state" style={{ padding: 30 }}><p>Δεν υπάρχουν δεδομένα</p></div>
                          ) : (
                            <table>
                              <thead><tr><th>Προϊόν</th><th>Τεμ.</th><th>Σύνολο</th></tr></thead>
                              <tbody>
                                {range.items.map((item, j) => (
                                  <tr key={j}>
                                    <td>{item.product_name}</td>
                                    <td>{parseFloat(item.quantity).toLocaleString('el-GR', { maximumFractionDigits: 3 })}</td>
                                    <td>€{parseFloat(item.total_price).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
